import { Grid, Box, FormControl, OutlinedInput, IconButton, InputLabel, InputAdornment, Button, Typography, CircularProgress } from '@mui/material'
import { useState } from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate, useLocation } from 'react-router-dom';
import { SuccessAlert } from '../../common/snackbar/success';
import { ErrorAlert } from '../../common/snackbar/error';
import { HttpRequest } from '../../HttpRequest';
import PasswordRules from '../../common/PasswordRules';
import { passwordMeetsPolicy } from '../../../utils/passwordPolicy';
import secureLocalStorage from 'react-secure-storage';

/**
 * Forced pin->password migration (ZED-2152): login returned
 * passwordSetupRequired with a restricted 15-minute token that only works on
 * /api/v1/users/password/setup. Set the password here, then log in again.
 */
const PasswordSetup = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const setupToken = location?.state?.setupToken;

    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorShow, setErrorShow] = useState({ state: false, message: '' });
    const [successShow, setSuccessShow] = useState({ state: false, message: '' });

    // Opened without a login (refresh, bookmark): the token lives only in
    // navigation state, so send the user back to log in again.
    if (!setupToken) {
        navigate('/login');
        return null;
    }

    const handleSubmit = async () => {
        if (!passwordMeetsPolicy(password)) {
            setErrorShow({ state: true, message: 'Password does not meet the requirements below.' });
            return;
        }
        if (password !== passwordConfirm) {
            setErrorShow({ state: true, message: 'Passwords do not match.' });
            return;
        }
        setLoading(true);
        // HttpRequest prefers a stored token over passed headers — clear any
        // stale one so the restricted setup token is the credential used.
        secureLocalStorage.removeItem('token');
        const response = await HttpRequest({
            method: 'POST',
            url: '/api/v1/users/password/setup',
            body: { newPassword: password },
            headers: { 'X-Authorization': setupToken },
        }).catch(() => null);
        setLoading(false);
        if (response?.status === 200) {
            setSuccessShow({ state: true, message: 'Password set. Log in with your new password.' });
            setTimeout(() => navigate('/login'), 1500);
        } else if (response?.status === 401 || response?.status === 403) {
            setErrorShow({ state: true, message: 'This session has expired. Please log in again.' });
            setTimeout(() => navigate('/login'), 2000);
        } else {
            setErrorShow({ state: true, message: response?.response?.message || 'Could not set password' });
        }
    };

    const eye = (
        <InputAdornment position="end">
            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <Grid container justifyContent="center" sx={{ minHeight: '70vh', alignContent: 'center', px: 2 }}>
            {errorShow.state && <ErrorAlert error={errorShow.state} message={errorShow.message} />}
            {successShow.state && <SuccessAlert sucess={successShow.state} message={successShow.message} />}
            <Grid item xs={12} sm={8} md={5} lg={4}>
                <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#032541' }}>Set your password</Typography>
                <Typography sx={{ fontSize: '14px', color: '#707070', mt: 1, mb: 3 }}>
                    Your account still uses a 4-digit PIN. Set a password to continue — you'll use it to log in from now on.
                </Typography>
                <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                    <InputLabel htmlFor="setup-password">New Password</InputLabel>
                    <OutlinedInput id="setup-password" label="New Password" type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password" value={password} inputProps={{ maxLength: 128 }}
                        onChange={(e) => setPassword(e.target.value.replace(/\s/g, ''))} endAdornment={eye} />
                </FormControl>
                <PasswordRules password={password} />
                <FormControl variant="outlined" fullWidth sx={{ mt: 1, mb: 3 }}>
                    <InputLabel htmlFor="setup-password-confirm">Confirm Password</InputLabel>
                    <OutlinedInput id="setup-password-confirm" label="Confirm Password" type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password" value={passwordConfirm} inputProps={{ maxLength: 128 }}
                        onChange={(e) => setPasswordConfirm(e.target.value.replace(/\s/g, ''))} />
                </FormControl>
                <Button fullWidth variant="contained" disabled={loading} onClick={handleSubmit}
                    sx={{ height: '48px', backgroundColor: '#032541', textTransform: 'none', '&:hover': { backgroundColor: '#021d30' } }}>
                    {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Set Password'}
                </Button>
            </Grid>
        </Grid>
    );
};

export default PasswordSetup;
